/**
 * Typisierter Hook für `GET /api/v1/calendar` (Spec 07, `domains/calendar`) —
 * **ein** Endpunkt für alle vier Arten (Sollstellung, Frist, Termin,
 * Aufgabe), bereits zusammengeführt, gebündelt und sortiert vom Server
 * (Regel 2, I9). Der Kalender selbst schreibt nie — Anlegen läuft über
 * `features/schedule/api.ts::useCreateDueDate`.
 *
 * `fetch` steht ausschließlich hier — nie in einer Komponente
 * (`apps/web/CLAUDE.md`).
 */

import { useQuery } from '@tanstack/react-query'

import type { components } from '@/lib/api-types'
import { request } from '@/lib/api'
import { keys } from '@/lib/query-keys'

export type CalendarResponse = components['schemas']['CalendarResponse']
export type CalendarWeek = components['schemas']['CalendarWeek']
export type CalendarDay = components['schemas']['CalendarDay']
export type CalendarEntry = components['schemas']['CalendarEntry']
export type CalendarSubject = components['schemas']['CalendarSubject']

/**
 * `GET /calendar?ab=` — vier Wochen ab Montag der ISO-Woche, in der `ab`
 * liegt. **Kein Client rechnet einen Wochenanfang aus** (Spec, „Das Fenster
 * ist vier Wochen ab Montag, und der Server bestimmt es"): `ab` geht
 * unverändert an den Server, der normalisiert auf den Montag — auch wenn das
 * übergebene Datum keiner ist. Fehlt `ab`, liefert der Server die laufende
 * Woche.
 */
export function useCalendar(ab: string | undefined, options: { readonly enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.calendar.window({ ab }),
    queryFn: ({ signal }) => {
      const query = ab === undefined ? '' : `?ab=${ab}`
      return request<CalendarResponse>(`/calendar${query}`, { signal })
    },
    // Die Listenansichten des Kalenders (Spec 45) brauchen das Fenster nicht.
    enabled: options.enabled ?? true,
  })
}
